<template>
    <div class="view-toggle-wrapper">
      <div class="view-toggle-container" :class="{ 'is-duck-view': modelValue }">
        <div class="toggle-option admin-option" @click="toggleView(false)" :class="{ 'active': !modelValue }">
          <v-icon icon="mdi-view-dashboard" class="mr-1"></v-icon>
          <span>Admin</span>
        </div>
        <div class="toggle-option duck-option" @click="toggleView(true)" :class="{ 'active': modelValue }">
          <v-icon icon="mdi-duck" class="mr-1"></v-icon>
          <span>Duck</span>
        </div>
        <div class="slider" :class="{ 'slide-right': modelValue }"></div>
      </div>
    </div>
  </template>
  
  <script setup lang="ts">
  import { computed } from 'vue';
  import { useRouter, useRoute } from 'vue-router';
  import { useDemoDuckStore } from '@/stores/demoDuckStore';
  
  defineProps({
    modelValue: {
      type: Boolean,
      default: false
    }
  });
  
  const emit = defineEmits(['update:modelValue']);
  
  const router = useRouter();
  const route = useRoute();
  const duckStore = useDemoDuckStore();
  
  // Determine if we're in the admin view based on the current route
  const isAdminView = computed(() => {
    return route.name === 'demo';
  });
  
  // Handle the toggle switch change
  const toggleView = (toDuckView: boolean) => {
    emit('update:modelValue', toDuckView);
    
    if (toDuckView) {
      // Toggle to Duck View
      if (isAdminView.value) {
        // We're in admin view, navigate to duck view
        // Initialize ducks if needed
        duckStore.initializeDucks();
        
        // Find the first duck to show
        const ducks = duckStore.ducks;
        if (ducks.length > 0) {
          router.push(`/demo/duck/${ducks[0].id}`);
        } else {
          console.error('No ducks available to view');
        }
      }
    } else {
      // Toggle to Admin View
      if (!isAdminView.value) {
        router.push('/demo');
      }
    }
  };
  </script>
  
  <style>
  .view-toggle-wrapper {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 1000;
  }
  
  .view-toggle-container {
    position: relative;
    display: flex;
    background: linear-gradient(135deg, rgba(255,247,214,0.9), rgba(255,236,168,0.95));
    border-radius: 30px;
    box-shadow: 0 6px 15px rgba(0,0,0,0.15), 
                0 2px 4px rgba(0,0,0,0.12), 
                0 0 20px rgba(255, 193, 7, 0.3);
    padding: 4px;
    width: 210px;
    height: 44px;
    overflow: hidden;
    transition: all 0.3s ease;
    backdrop-filter: blur(5px);
    border: 1px solid rgba(255, 206, 50, 0.5);
    transform: translateY(0);
    animation: float 3s ease-in-out infinite;
  }
  
  .view-toggle-container:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0,0,0,0.2), 
                0 3px 6px rgba(0,0,0,0.15), 
                0 0 25px rgba(255, 193, 7, 0.4);
  }
  
  .toggle-option {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 50%;
    height: 100%;
    position: relative;
    z-index: 2;
    cursor: pointer;
    color: rgba(0,0,0,0.6);
    font-weight: 600;
    transition: all 0.3s ease;
    border-radius: 26px;
    user-select: none;
  }
  
  .toggle-option.active {
    color: #fff;
    text-shadow: 0 1px 2px rgba(0,0,0,0.1);
  }
  
  .toggle-option:not(.active):hover {
    color: rgba(0,0,0,0.8);
  }
  
  .slider {
    position: absolute;
    top: 4px;
    left: 4px;
    height: calc(100% - 8px);
    width: calc(50% - 4px);
    background: linear-gradient(135deg, #ffb700, #ff9d00);
    border-radius: 26px;
    transition: all 0.4s cubic-bezier(0.68, -0.55, 0.27, 1.55);
    z-index: 1;
    box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  }
  
  .slider.slide-right {
    left: calc(50% + 0px);
  }
  
  .admin-option {
    padding-right: 4px;
  }
  
  .duck-option {
    padding-left: 4px;
  }
  
  .is-duck-view {
    background: linear-gradient(135deg, rgba(255,247,214,0.95), rgba(255,224,122,0.98));
  }
  
  @keyframes float {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-5px);
    }
  }
  
  /* Duck icon when active */
  .duck-option.active .v-icon {
    animation: waddle 1s ease-in-out infinite;
    transform-origin: bottom center;
  }
  
  @keyframes waddle {
    0%, 100% {
      transform: rotate(0deg);
    }
    25% {
      transform: rotate(-5deg);
    }
    75% {
      transform: rotate(5deg);
    }
  }
  
  /* Responsive adjustments */
  @media (max-width: 640px) {
    .view-toggle-container {
      width: 180px;
      height: 40px;
    }
    
    .toggle-option {
      font-size: 14px;
    }
  }
  </style>